import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { MaintenanceListTableDisplay } from '@/components/sections/service/maintenance-list/table';
import {
  LABEL_WIDTH,
  StyledRowItemsWrapper,
} from '@/components/sections/service/maintenance-list/table/full';
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from '@/components/shared/element/scroll-area';
import { Box } from '@/components/shared/layout/box';
import { useWindowSize } from '@/components/shared/utility/hooks';
import { styled } from '@/stitches.config';

interface MaintenanceListTableFullScrollBarProps {
  thumbSize: string;
  tableItems: any[];
  selectedIndex?: number;
  tableDisplay?: MaintenanceListTableDisplay;
  onScroll: (e: any) => void;
}

export const StyledScrollSnapWrapper = styled('div', {
  overflow: 'hidden',
  '& .scrollable-area': {
    transform: 'translateX(calc(var(--table-scroll) * -1))',
    willChange: 'transform',
  },
});

const StyledOverflowShadow = styled(Box, {
  position: 'absolute',
  zIndex: 1,
  top: 0,
  width: '40px',
  height: 'calc(100% - 38px)',
  transition: 'opacity 0.2s ease',
  variants: {
    side: {
      left: {
        left: 'calc(var(--label-width) - 38px)',
        clipPath: 'inset(0px -20px 0px 0)',
        boxShadow: 'rgba(101, 101, 101, 0.3) 5px 1px 9px',
      },
      right: {
        right: '-40px',
        clipPath: 'inset(0px 0px 0px -40px)',
        boxShadow: 'rgba(101, 101, 101, 0.3) -5px 1px 9px',
        '@maxlg': {
          boxShadow: 'rgba(101, 101, 101, 0.5) -10px 0px 14px',
        },
      },
    },
    show: {
      true: {
        opacity: 1,
      },
      false: {
        opacity: 0,
      },
    },
  },
  defaultVariants: {
    show: false,
  },
});

enum ScrollMoreShadow {
  Left,
  Right,
}

export const MaintenanceListTableFullScrollBar = ({
  thumbSize,
  tableItems,
  selectedIndex = -1,
  tableDisplay,
  onScroll,
}: MaintenanceListTableFullScrollBarProps) => {
  const windowSize = useWindowSize();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const [showScrollMoreShadow, setShowScrollMoreShadow] = useState<boolean[]>([
    false,
    false,
  ]);

  const handleShowScrollMoreShadow = useCallback(
    (scrollLeft: number, scrollWidth: number, containerWidth: number) => {
      const currentShowResults = showScrollMoreShadow.slice();
      const scrollEndPosition = scrollWidth;
      const isStartOfScroll = scrollLeft === 0;
      const isEndOfScroll = scrollLeft + containerWidth === scrollEndPosition;
      if (isStartOfScroll && isEndOfScroll) {
        setShowScrollMoreShadow([false, false]);
      } else if (isStartOfScroll && !isEndOfScroll) {
        setShowScrollMoreShadow([false, true]);
      } else if (
        !isStartOfScroll &&
        !isEndOfScroll &&
        !(
          currentShowResults[ScrollMoreShadow.Left] &&
          currentShowResults[ScrollMoreShadow.Right]
        )
      ) {
        // optimize set shadow state
        setShowScrollMoreShadow([true, true]);
      } else if (!isStartOfScroll && isEndOfScroll) {
        setShowScrollMoreShadow([true, false]);
      }
    },
    [setShowScrollMoreShadow, showScrollMoreShadow]
  );

  const handleOnTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    onScroll(e);
    const el = e.target as HTMLDivElement;
    if (el) {
      handleShowScrollMoreShadow(el.scrollLeft, el.scrollWidth, el.clientWidth);
    }
  };

  useEffect(() => {
    if (
      tableDisplay === 'brief' ||
      selectedIndex === -1 ||
      !scrollRef.current ||
      !itemsRef.current
    )
      return;

    const items = itemsRef.current.children as any;
    const el = items[selectedIndex] as HTMLDivElement;
    if ((windowSize.width ?? 0) <= 480) {
      scrollRef.current.scrollLeft =
        el.offsetLeft - scrollRef.current.offsetLeft - el.clientWidth;
    } else {
      const wrapperWidth =
        itemsRef.current.parentElement?.parentElement?.clientWidth ?? 0;
      const currentElOffsetLeft = el.offsetLeft - LABEL_WIDTH.md;
      scrollRef.current.scrollLeft =
        currentElOffsetLeft - (wrapperWidth - el.clientWidth) / 2;
    }
  }, [selectedIndex, scrollRef, itemsRef, tableDisplay, windowSize.width]);

  useEffect(() => {
    const scrollAreaEl = document.querySelector('.scrollable-area');
    if (!scrollAreaEl) return;

    handleShowScrollMoreShadow(
      scrollAreaEl.scrollLeft,
      scrollAreaEl.scrollWidth,
      scrollAreaEl.clientWidth
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box position="absolute" inset="0" zIndex="9">
      <ScrollArea
        type="auto"
        css={{
          paddingLeft: 'var(--label-width)',
        }}
      >
        <ScrollAreaViewport
          ref={scrollRef}
          onScroll={handleOnTableScroll}
          css={{
            scrollSnapType: 'x mandatory',
            '@hover': {
              '&:hover': {
                [`+ ${ScrollAreaScrollbar}`]: {
                  [`${ScrollAreaThumb}`]: {
                    bc: '$gray800',
                  },
                },
              },
            },
          }}
        >
          <StyledRowItemsWrapper
            ref={itemsRef}
            css={{
              gridTemplateRows: thumbSize,
            }}
          >
            {tableItems.map((_, index) => (
              <div
                key={`scrollItem${index}`}
                style={{
                  opacity: 0,
                  scrollSnapAlign: index % 2 ? undefined : 'start',
                }}
              />
            ))}
          </StyledRowItemsWrapper>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar
          orientation="horizontal"
          css={{
            '--size': thumbSize,
            br: '$rounded-1',
            background: '$gray400',
            '@maxlg': {
              marginLeft: '$space-4',
              marginRight: '$space-4',
            },
            '@hover': {
              '&:hover': {
                cursor: 'ew-resize',
                [`${ScrollAreaThumb}`]: {
                  bc: '$gray800',
                },
              },
            },
          }}
        >
          <ScrollAreaThumb
            css={{
              br: '$rounded-1',
              transitionProperty: 'background-color',
              transitionDelay: 'var(--transition-delay)',
              transitionDuration: 'var(--transition-duration)',
              transitionTimingFunction: 'var(--transition-easing)',
            }}
          />
        </ScrollAreaScrollbar>
        <StyledOverflowShadow
          side="left"
          show={showScrollMoreShadow[ScrollMoreShadow.Left]}
        />
        <StyledOverflowShadow
          side="right"
          show={showScrollMoreShadow[ScrollMoreShadow.Right]}
        />
      </ScrollArea>
    </Box>
  );
};

MaintenanceListTableFullScrollBar.displayName =
  'SectionMaintenanceListTableFullScrollBar';
